// src/components/customer/CustomerLayout.jsx

import React, { useState, useEffect, useRef } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  FiShoppingCart, FiUser, FiMenu, FiX, FiSearch,
  FiPackage, FiLogOut, FiSettings, FiPhone, FiMail, FiMapPin,
  FiHeart, FiHome, FiGrid, FiShoppingBag, FiInfo, FiFileText,
} from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import toast from "react-hot-toast";
import logo from "../../assests/logo.png";
import footerTheme from "../../assests/theme.png";
import { QRCodeSVG } from "qrcode.react";
import { getSocialLinks } from "../../services/firestoreService";
import { getPlatformIcon } from "../../constants/socialPlatforms";
import { FaWhatsapp } from "react-icons/fa";

const SITE_URL = "https://www.tripuraagro.com";

const NAV_LINKS = [
  { label: "Home",       to: "/",           icon: FiHome        },
  { label: "Categories", to: "/categories", icon: FiGrid        },
  { label: "Products",   to: "/products",   icon: FiShoppingBag },
  { label: "About",      to: "/about",      icon: FiInfo        },
  { label: "Blog",       to: "/blog",       icon: FiFileText,   badge: "New" },
  { label: "Contact",    to: "/contact",    icon: FiPhone       },
];

export default function CustomerLayout() {
  const { user, userProfile, isAdmin, logout } = useAuth();
  const { count } = useCart();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [menuOpen,    setMenuOpen]    = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenu,    setUserMenu]    = useState(false);
  const [socialLinks, setSocialLinks] = useState([]); // populated from Firestore, managed in Admin → Follow Us
  const userMenuRef = useRef(null);

  // Prefer the Firestore profile's name (kept in sync by MyAccount via
  // refreshProfile) over Firebase Auth's displayName, so an update on the
  // My Account page reflects here immediately instead of needing a re-login.
  const displayName = userProfile?.name || user?.displayName || "";

  useEffect(() => {
    getSocialLinks().then(setSocialLinks).catch(() => setSocialLinks([]));
  }, []);

  // Close the account dropdown when clicking anywhere outside of it
  useEffect(() => {
    if (!userMenu) return;
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenu]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const handleLogout = async () => {
    await logout();
    setUserMenu(false);
    toast.success("Logged out");
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#f7f3e3" }}>

      {/* ── Global font-family injection (nav + entire layout) ── */}
      <style>{`
        body :where(p, a, span, small, li, label, legend, summary, button, input, textarea, select, option, table, thead, tbody, tfoot, tr, th, td, caption, blockquote, cite, em, strong, b, i, div, .button, .btn, .field__input, .form__label, .product-form__submit, .price, .badge, .card__information, .product__text, .header__menu-item, .menu-drawer__menu-item, .footer-block, .rte):not(.jdgm-widget):not(.jdgm-widget *):not([class*="jdgm"]):not([class*="jdgm"] *):not(.spr-container):not(.spr-container *):not(.icon):not(.icon *):not([class^="icon-"]):not([class*=" icon-"]):not([class*="fa-"]):not([class*="fa-"] *):not(svg):not(svg *):not(path):not(path *) {
          font-family: var(--custom-body-font-family) !important;
          font-weight:700 !important;
          font-style: normal !important;
        }

        @keyframes marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          display: flex;
          align-items: center;
          width: max-content;
          animation: marquee 35s linear infinite;
        }
        .marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* ── Topbar Marquee Slider ── */}
      <div className="bg-[#1a3d1a] overflow-hidden relative" style={{ height: "38px" }}>
        {/* Left / right fade edges */}
        <div className="pointer-events-none absolute left-0 top-0 h-full w-16 bg-gradient-to-r from-[#1a3d1a] to-transparent z-10" />
        <div className="pointer-events-none absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-[#1a3d1a] to-transparent z-10" />

        <div className="marquee-track h-full">
          {[0, 1].map((copy) => (
            <span key={copy} className="flex items-center h-full">
              {[
                "CASH ON DELIVERY AVAILABLE",
                "100% NATURAL & FRESH PRODUCTS",
                "DIRECT FROM FARMERS TO YOUR DOORSTEP",
                "QUALITY CERTIFIED AGRICULTURAL PRODUCTS",
              ].map((msg, i) => (
                <span key={i} className="flex items-center h-full">
                  <span
                    className="whitespace-nowrap text-white font-bold tracking-widest px-8"
                    style={{ fontSize: "12.5px", letterSpacing: "0.12em" }}
                  >
                    {msg}
                  </span>
                  <span
                    className="text-lime-400 flex-shrink-0 select-none"
                    style={{ fontSize: "14px", lineHeight: 1 }}
                    aria-hidden="true"
                  >
                    ✦
                  </span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      <header className="sticky top-0 z-40" style={{ background: "#f7f3e3", borderBottom: "1.5px solid #e2dcc8" }}>

        {/* ── Top row: Left actions | Logo center | Search + account right ── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex items-center justify-between h-[70px] sm:h-[80px] gap-1">

            {/* LEFT: mobile menu toggle / desktop left nav placeholder */}
            <div className="flex items-center gap-1 min-w-0">
              {/* Mobile hamburger */}
              <button onClick={() => setMenuOpen(!menuOpen)}
                className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-gray-100 text-gray-600 lg:hidden outline-none flex-shrink-0">
                {menuOpen ? <FiX className="w-5 h-5" /> : <FiMenu className="w-5 h-5" />}
              </button>

              {/* Desktop: wishlist + cart on left */}
              <div className="hidden lg:flex items-center gap-1">
                {user && (
                  <Link to="/wishlist"
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors"
                    title="My Wishlist">
                    <FiHeart className="w-[18px] h-[18px]" />
                  </Link>
                )}
                <Link to="/cart"
                  className="relative w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors">
                  <FiShoppingCart className="w-[18px] h-[18px]" />
                  {count > 0 && (
                    <span className="absolute top-1 right-1 w-[15px] h-[15px] bg-[#3a5c2f] text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {count > 9 ? "9+" : count}
                    </span>
                  )}
                </Link>
              </div>
            </div>

            {/* CENTER: Logo — sits in its own grid column (1fr), centered within whatever
                space is left over. A grid column can never be overlapped by the left/right
                columns, so the search icon can no longer get hidden behind the logo when
                the right side gets wider (e.g. once a user is logged in). */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center overflow-hidden">
              <Link to="/" className="outline-none focus-visible:ring-2 focus-visible:ring-green-600 rounded-lg flex items-center justify-center">
                <img
                  src={logo}
                  alt="Tripura Agro Logo"
                  className="h-14 sm:h-24 w-auto max-w-[100px] sm:max-w-[200px] object-contain"
                />
              </Link>
            </div>

            {/* RIGHT: Search + account */}
            <div className="flex items-center gap-1 sm:gap-2 justify-end min-w-0">

              <form onSubmit={handleSearch}
                className="hidden md:flex items-center h-[42px] overflow-hidden"
                style={{
                  background: "transparent",
                  borderBottom: "1.5px solid #b8b098",
                  minWidth: "240px",
                  maxWidth: "320px",
                }}
              >
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="border-none outline-none flex-1 pl-2 pr-2 h-full"
                  style={{ fontSize: "14px", color: "#2d3a1a", minWidth: 0, background: "transparent" }}
                />
                <button
                  type="submit"
                  className="flex-shrink-0 flex items-center justify-center h-full px-3 transition-opacity hover:opacity-70"
                  style={{ background: "transparent", minWidth: "36px" }}
                >
                  <FiSearch className="w-4 h-4" style={{ color: "#4a6e30" }} />
                </button>
              </form>

              {/* Mobile: search icon */}
              <button
                className="md:hidden w-8 h-8 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100"
                onClick={() => setMenuOpen(true)}
              >
                <FiSearch className="w-4 h-4" />
              </button>

              {/* Mobile: cart */}
              <Link to="/cart"
                className="relative lg:hidden w-8 h-8 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors">
                <FiShoppingCart className="w-[18px] h-[18px]" />
                {count > 0 && (
                  <span className="absolute top-1 right-1 w-[15px] h-[15px] bg-[#3a5c2f] text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                    {count > 9 ? "9+" : count}
                  </span>
                )}
              </Link>

              {/* Account */}
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenu(!userMenu)}
                    className="flex items-center gap-1 sm:gap-1.5 pl-0.5 pr-1.5 sm:pl-1 sm:pr-3 py-0.5 sm:py-1 rounded-full border transition-all"
                    style={{ border: "1.5px solid #d4d4b8", background: "#fafaf2" }}
                  >
                    <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#3a5c2f] flex items-center justify-center text-[10px] sm:text-[12px] font-bold text-white flex-shrink-0">
                      {(displayName || user.email)[0].toUpperCase()}
                    </div>
                    <span className="text-[12px] font-semibold hidden sm:block max-w-[60px] truncate" style={{ color: "#2d3a1a" }}>
                      {displayName?.split(" ")[0] || "Account"}
                    </span>
                    <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {userMenu && (
                    <div className="absolute right-0 mt-2 w-44 sm:w-52 bg-white rounded-2xl border border-gray-100 shadow-xl py-1.5 z-50 overflow-hidden">
                      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                        <div className="w-10 h-10 rounded-full bg-[#3a5c2f] flex items-center justify-center text-[14px] font-bold text-white flex-shrink-0">
                          {(displayName || user.email)[0].toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-gray-900 truncate">{displayName || "User"}</p>
                          <p className="text-[11px] text-gray-400 truncate mt-0.5">{user.email}</p>
                        </div>
                      </div>
                      <div className="py-1">
                        <Link to="/orders" onClick={() => setUserMenu(false)}
                          className="flex items-center gap-2.5 px-4 py-[9px] text-[13px] text-gray-700 hover:bg-gray-50 transition-colors">
                          <FiPackage className="w-4 h-4 text-gray-400" /> My orders
                        </Link>
                        <Link to="/wishlist" onClick={() => setUserMenu(false)}
                          className="flex items-center gap-2.5 px-4 py-[9px] text-[13px] text-gray-700 hover:bg-gray-50 transition-colors">
                          <FiHeart className="w-4 h-4 text-gray-400" /> My wishlist
                        </Link>
                        <Link to="/account" onClick={() => setUserMenu(false)}
                          className="flex items-center gap-2.5 px-4 py-[9px] text-[13px] text-gray-700 hover:bg-gray-50 transition-colors">
                          <FiUser className="w-4 h-4 text-gray-400" /> My account
                        </Link>
                      </div>
                      {isAdmin() && (
                        <>
                          <div className="h-px bg-gray-100 mx-3" />
                          <div className="py-1">
                            <Link to="/admin" onClick={() => setUserMenu(false)}
                              className="flex items-center gap-2.5 px-4 py-[9px] text-[13px] text-gray-700 hover:bg-gray-50 transition-colors">
                              <FiSettings className="w-4 h-4 text-gray-400" /> Admin panel
                            </Link>
                          </div>
                        </>
                      )}
                      <div className="h-px bg-gray-100 mx-3" />
                      <div className="py-1">
                        <button onClick={handleLogout}
                          className="flex items-center gap-2.5 px-4 py-[9px] text-[13px] text-red-600 hover:bg-red-50 w-full text-left transition-colors">
                          <FiLogOut className="w-4 h-4" /> Log out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link to="/login"
                  className="hidden sm:flex items-center gap-1.5 h-9 px-4 rounded-full text-[13px] font-semibold transition-colors"
                  style={{ background: "#3a5c2f", color: "#fff" }}>
                  <FiUser className="w-3.5 h-3.5" /> Login
                </Link>
              )}
            </div>
          </div>
        </div>

        <nav className="hidden lg:block" style={{ borderTop: "1.5px solid #e2dcc8", background: "#f7f3e3" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-0">
            {NAV_LINKS.map(({ label, to, icon: Icon, badge }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className="relative group flex items-center gap-2 px-5 py-3 whitespace-nowrap outline-none transition-colors"
                  style={{
                    fontSize: "15px",
                    fontWeight: 700,
                    color: active ? "#3a5c2f" : "#3d3d2a",
                    letterSpacing: "0.01em",
                  }}
                >
                  <Icon
                    className="w-3.5 h-3.5 flex-shrink-0 transition-colors"
                    style={{ color: active ? "#3a5c2f" : "#8a8a6a" }}
                  />
                  {label}
                  {badge && (
                    <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full" style={{ background: "#3a5c2f", color: "#fff" }}>
                      {badge}
                    </span>
                  )}
                  {/* Underline indicator */}
                  <span
                    className="absolute bottom-0 left-4 right-4 h-[2px] rounded-full transition-all duration-300 origin-center"
                    style={{
                      background: "#3a5c2f",
                      transform: active ? "scaleX(1)" : "scaleX(0)",
                    }}
                  />
                  {/* Hover underline */}
                  <span
                    className="absolute bottom-0 left-4 right-4 h-[2px] rounded-full transition-all duration-300 origin-center opacity-0 group-hover:opacity-100 group-hover:scaleX-100"
                    style={{ background: "#8ab87a" }}
                  />
                </Link>
              );
            })}
          </div>
        </nav>

        {/* ── Mobile menu ── */}
        {menuOpen && (
          <div className="lg:hidden border-t px-4 py-4 space-y-0.5" style={{ borderColor: "#e2dcc8", background: "#f7f3e3" }}>
            <form onSubmit={handleSearch} className="mb-4">
              <div className="flex items-center gap-2 px-3.5 h-10 transition-all"
                style={{ background: "#f5f5e8", border: "1.5px solid #d4d4b8", borderRadius: "999px" }}>
                <FiSearch className="w-4 h-4 flex-shrink-0" style={{ color: "#5a6e40" }} />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products…"
                  className="bg-transparent border-none outline-none w-full"
                  style={{ fontSize: "13px", color: "#2d3a1a" }}
                />
              </div>
            </form>
            {NAV_LINKS.map(({ label, to, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-semibold transition-colors"
                style={{
                  color: location.pathname === to ? "#3a5c2f" : "#4a4a30",
                  background: location.pathname === to ? "#f0f5ea" : "transparent",
                }}>
                <Icon className="w-4 h-4" style={{ color: location.pathname === to ? "#3a5c2f" : "#8a8a6a" }} />
                {label}
              </Link>
            ))}
            {!user && (
              <Link to="/login" onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 mt-3 py-2.5 rounded-full font-semibold text-[13px]"
                style={{ background: "#3a5c2f", color: "#fff" }}>
                <FiUser className="w-4 h-4" /> Login
              </Link>
            )}
          </div>
        )}
      </header>

      {/* ── Page content ── */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* ══ FOOTER ══ */}
      <footer
        style={{
          marginTop: "3rem",
          backgroundImage: `linear-gradient(rgba(10,30,11,0.90), rgba(10,30,11,0.94)), url(${footerTheme})`,
          backgroundSize: "cover",
          backgroundPosition: "top center",
          backgroundRepeat: "no-repeat",
        }}
      >

        {/* ── Top accent line ── */}
        <div style={{ height: "3px", background: "linear-gradient(90deg, #4a6e30, #a8d060, #4a6e30)" }} />

        {/* ── Newsletter / tagline strip ── */}
        <div style={{ background: "#162f18", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p style={{ color: "#b8d89a", fontSize: "0.82rem", fontWeight: 600, letterSpacing: "0.06em" }}>
              🌿 &nbsp;Pure · Natural · Trusted — Farm to Doorstep across India
            </p>
            <a
              href="https://wa.me/919778553533"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 flex-shrink-0"
              style={{
                background: "#25D366",
                color: "#fff",
                fontSize: "0.78rem",
                fontWeight: 700,
                padding: "8px 18px",
                borderRadius: "999px",
                letterSpacing: "0.04em",
              }}
            >
              <FaWhatsapp /> Chat on WhatsApp
            </a>
          </div>
        </div>

        {/* ── Main footer body ── */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

            {/* Col 1: Brand + Social */}
            <div>
              {/* Logo + name */}
              <div className="flex items-center gap-3 mb-4">
                <div style={{ background: "#fff", borderRadius: "12px", width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                  <img src={logo} alt="Tripura Agro" style={{ width: 36, height: 36, objectFit: "contain" }} />
                </div>
                <div>
                  <p style={{ color: "#ffffff", fontSize: "1rem", fontWeight: 800, lineHeight: 1.2 }}>Tripura Agro</p>
                  <p style={{ color: "#7ab84a", fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.06em" }}>Quality Agricultural Products</p>
                </div>
              </div>

              {/* QR code */}
              {/* <div style={{ marginBottom: "16px" }}>
                <p style={{ color: "#6a9a50", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "8px" }}>Scan to Visit Our App</p>
                <div style={{ background: "#fff", borderRadius: "10px", padding: "8px", display: "inline-block" }}>
                  <QRCodeSVG value={SITE_URL} size={88} bgColor="#ffffff" fgColor="#14532d" level="H"
                    imageSettings={{ src: logo, x: undefined, y: undefined, height: 18, width: 18, excavate: true }} />
                </div>
              </div> */}

              {/* Social — hidden entirely if no links are configured yet in Admin → Follow Us */}
              {socialLinks.length > 0 && (
                <>
                  <p style={{ color: "#6a9a50", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "8px" }}>Follow Us</p>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {socialLinks.map(({ id, platform, href, label }) => {
                      const Icon = getPlatformIcon(platform);
                      return (
                        <a key={id} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                          style={{
                            width: 32, height: 32, borderRadius: "8px",
                            border: "1px solid rgba(255,255,255,0.12)",
                            background: "rgba(255,255,255,0.05)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            color: "#a0c880", fontSize: "13px",
                            transition: "background 0.2s, color 0.2s",
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background="#4a6e30"; e.currentTarget.style.color="#fff"; }}
                          onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.05)"; e.currentTarget.style.color="#a0c880"; }}
                        >
                          <Icon />
                        </a>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Col 2: Quick Links */}
            <div>
              <p style={{ color: "#ffffff", fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "16px", borderLeft: "3px solid #4a6e30", paddingLeft: "10px" }}>
                Quick Links
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                {[
                  { label: "Home", to: "/" },
                  { label: "About Us", to: "/about" },
                  { label: "Categories", to: "/categories" },
                  { label: "Products", to: "/products" },
                  { label: "Blog", to: "/blog" },
                  { label: "Careers", to: "/careers" },
                  { label: "Contact Us", to: "/contact" },
                ].map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to} style={{ color: "#9ab880", fontSize: "0.8rem", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px", transition: "color 0.2s, paddingLeft 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.color="#d0f0a0"; e.currentTarget.style.paddingLeft="6px"; }}
                      onMouseLeave={e => { e.currentTarget.style.color="#9ab880"; e.currentTarget.style.paddingLeft="0"; }}
                    >
                      <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: "#4a6e30", flexShrink: 0 }} />
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 3: Legal */}
            <div>
              <p style={{ color: "#ffffff", fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "16px", borderLeft: "3px solid #4a6e30", paddingLeft: "10px" }}>
                Legal
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}>
                {[
                  { label: "Privacy Policy", to: "/privacy" },
                  { label: "Terms & Conditions", to: "/terms" },
                  { label: "Shipping Policy", to: "/shipping-policy" },
                  { label: "Refund Policy", to: "/refund-policy" },
                ].map(({ label, to }) => (
                  <li key={to}>
                    <Link to={to} style={{ color: "#9ab880", fontSize: "0.8rem", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px", transition: "color 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.color="#d0f0a0"; }}
                      onMouseLeave={e => { e.currentTarget.style.color="#9ab880"; }}
                    >
                      <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: "#4a6e30", flexShrink: 0 }} />
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Col 4: Contact */}
            <div>
              <p style={{ color: "#ffffff", fontSize: "0.8rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "16px", borderLeft: "3px solid #4a6e30", paddingLeft: "10px" }}>
                Get in Touch
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {[
                  { icon: FiPhone, label: "Call Us", value: "+91 97785 53533", href: "tel:+919778553533" },
                  { icon: FaWhatsapp, label: "WhatsApp", value: "+91 97785 53533", href: "https://wa.me/919778553533" },
                  { icon: FiMail, label: "Email", value: "sandeep@tripuraagro.com", href: "mailto:sandeep@tripuraagro.com" },
                  { icon: FiMapPin, label: "Address", value: "1st Floor, Panigrahy Complex, Janata Lane, Digapahandi, Ganjam, Odisha", href: null },
                ].map(({ icon: Icon, label, value, href }) => {
                  const inner = (
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                      <div style={{ width: 30, height: 30, borderRadius: "8px", background: "rgba(74,110,48,0.35)", border: "1px solid rgba(122,184,74,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                        <Icon size={13} style={{ color: "#a0d060" }} />
                      </div>
                      <div>
                        <p style={{ color: "#6a9a50", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 2 }}>{label}</p>
                        <p style={{ color: "#c0dca0", fontSize: "0.76rem", lineHeight: 1.5 }}>{value}</p>
                      </div>
                    </div>
                  );
                  return href ? (
                    <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" style={{ textDecoration: "none" }}>{inner}</a>
                  ) : (
                    <div key={label}>{inner}</div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>

        {/* ── Bottom bar ── */}
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p style={{ color: "#5a7a42", fontSize: "0.72rem" }}>
              © {new Date().getFullYear()} Tripura Agro Industries. All Rights Reserved.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ display: "flex", gap: "16px" }}>
                {[
                  { label: "Privacy", to: "/privacy" },
                  { label: "Terms", to: "/terms" },
                  { label: "Support", to: "/contact" },
                ].map(({ label, to }) => (
                  <Link key={to} to={to} style={{ color: "#5a7a42", fontSize: "0.72rem", textDecoration: "none" }}
                    onMouseEnter={e => e.currentTarget.style.color="#a0d060"}
                    onMouseLeave={e => e.currentTarget.style.color="#5a7a42"}
                  >{label}</Link>
                ))}
              </div>
              {socialLinks.length > 0 && (
                <>
                  <span style={{ color: "rgba(255,255,255,0.1)" }}>|</span>
                  <div style={{ display: "flex", gap: "10px" }}>
                    {socialLinks.map(({ id, platform, href, label }) => {
                      const Icon = getPlatformIcon(platform);
                      return (
                        <a key={id} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
                          style={{ color: "#4a6e30", fontSize: "12px", transition: "color 0.2s" }}
                          onMouseEnter={e => e.currentTarget.style.color="#a0d060"}
                          onMouseLeave={e => e.currentTarget.style.color="#4a6e30"}
                        >
                          <Icon />
                        </a>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      </footer>

      {/* ── Floating WhatsApp Button (all pages) ── */}
      <style>{`
        @keyframes wa-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(37,211,102,0.55); }
          70%  { box-shadow: 0 0 0 14px rgba(37,211,102,0); }
          100% { box-shadow: 0 0 0 0 rgba(37,211,102,0); }
        }
        .wa-float {
          position: fixed;
          bottom: 24px;
          right: 24px;
          z-index: 9999;
          width: 54px;
          height: 54px;
          border-radius: 50%;
          background: #25D366;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 16px rgba(37,211,102,0.45);
          animation: wa-pulse 2.2s ease-out infinite;
          transition: transform 0.2s, background 0.2s;
          text-decoration: none;
        }
        .wa-float:hover {
          transform: scale(1.1);
          background: #20bf5b;
          animation: none;
          box-shadow: 0 6px 24px rgba(37,211,102,0.55);
        }
        .wa-float svg {
          width: 28px;
          height: 28px;
          fill: #fff;
        }
      `}</style>
      <a
        href="https://wa.me/919778553533"
        target="_blank"
        rel="noopener noreferrer"
        className="wa-float"
        aria-label="Chat on WhatsApp"
      >
        {/* Official WhatsApp SVG icon */}
        <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
          <path d="M16.003 2.667C8.637 2.667 2.667 8.637 2.667 16c0 2.363.627 4.672 1.816 6.693L2.667 29.333l6.844-1.779A13.274 13.274 0 0016.003 29.333C23.37 29.333 29.333 23.363 29.333 16S23.37 2.667 16.003 2.667zm0 24.267a11.011 11.011 0 01-5.617-1.543l-.402-.239-4.063 1.057 1.083-3.945-.262-.406A10.963 10.963 0 015.003 16c0-6.065 4.935-11 11-11s11 4.935 11 11-4.935 11-11 11zm6.04-8.23c-.33-.165-1.952-.963-2.254-1.073-.303-.11-.523-.165-.743.165-.22.33-.851 1.073-1.043 1.293-.193.22-.385.247-.715.082-.33-.165-1.394-.514-2.655-1.638-.981-.875-1.643-1.956-1.836-2.286-.193-.33-.021-.508.145-.672.15-.148.33-.385.495-.578.165-.193.22-.33.33-.55.11-.22.055-.413-.027-.578-.083-.165-.743-1.793-1.018-2.454-.268-.643-.54-.556-.743-.566l-.633-.011c-.22 0-.578.082-.88.413-.303.33-1.155 1.128-1.155 2.751s1.182 3.19 1.347 3.41c.165.22 2.326 3.553 5.638 4.983.788.34 1.403.543 1.882.695.79.252 1.51.216 2.078.131.634-.094 1.952-.798 2.227-1.569.275-.771.275-1.432.193-1.569-.083-.138-.303-.22-.633-.385z"/>
        </svg>
      </a>

    </div>
  );
}